import { z } from "zod";

import { JobCategory } from "@/api/generated";

import { annualIncomeSchema } from "./annualIncome";
import {
  FAMILY_NAME_REQUIRED_MESSAGE,
  FIRST_NAME_REQUIRED_MESSAGE,
  FAMILY_NAME_KANA_REQUIRED_MESSAGE,
  FIRST_NAME_KANA_REQUIRED_MESSAGE,
  KANA_FORMAT_MESSAGE,
  createRequiredMessage,
  createSelectRequiredMessage,
  NAME_MAX_LENGTH_MESSAGE,
  HALF_WIDTH_NUMBER_FORMAT_MESSAGE,
  PRICE_LIMIT_MESSAGE,
  INTEGER_FORMAT_MESSAGE,
  NON_NEGATIVE_MESSAGE,
} from "./message";
import {
  NAME_KANA_REGEX,
  MAX_NAME_LENGTH,
  HALF_WIDTH_NUMBER_REGEX,
  isWithinIntLimit,
  isInteger,
  isNonNegative,
} from "./patterns";

// 現在のお住まいについてフォームスキーマ
export const accountResidenceSchema = z.object({
  address_prefecture: z.string().min(1, createSelectRequiredMessage("お住まいの地域（都道府県）")),
  current_residence_type_id: z
    .number()
    .nullable()
    .refine((val) => val, {
      message: createSelectRequiredMessage("お住まいの家"),
    }),
  home_loan_amount: z
    .string()
    .min(1, createRequiredMessage("住宅ローン残債"))
    .refine(isInteger, { message: INTEGER_FORMAT_MESSAGE })
    .refine(isWithinIntLimit, { message: PRICE_LIMIT_MESSAGE })
    .refine(isNonNegative, { message: NON_NEGATIVE_MESSAGE }),
  monthly_rent_payment_amount: z
    .string()
    .min(1, createRequiredMessage("家賃"))
    .refine(isInteger, { message: INTEGER_FORMAT_MESSAGE })
    .refine(isWithinIntLimit, { message: PRICE_LIMIT_MESSAGE })
    .refine(isNonNegative, { message: NON_NEGATIVE_MESSAGE }),
});

// あなたのことを教えてくださいフォームスキーマ
export const accountBirthSchema = z.object({
  family_name: z.string().min(1, FAMILY_NAME_REQUIRED_MESSAGE).max(MAX_NAME_LENGTH, NAME_MAX_LENGTH_MESSAGE),
  first_name: z.string().min(1, FIRST_NAME_REQUIRED_MESSAGE).max(MAX_NAME_LENGTH, NAME_MAX_LENGTH_MESSAGE),
  family_name_kana: z
    .string()
    .min(1, FAMILY_NAME_KANA_REQUIRED_MESSAGE)
    .max(MAX_NAME_LENGTH, NAME_MAX_LENGTH_MESSAGE)
    .regex(NAME_KANA_REGEX, KANA_FORMAT_MESSAGE),
  first_name_kana: z
    .string()
    .min(1, FIRST_NAME_KANA_REQUIRED_MESSAGE)
    .max(MAX_NAME_LENGTH, NAME_MAX_LENGTH_MESSAGE)
    .regex(NAME_KANA_REGEX, KANA_FORMAT_MESSAGE),
  birth_year: z
    .number()
    .nullable()
    .refine((val) => val, {
      message: createSelectRequiredMessage("年"),
    }),
  birth_month: z
    .number()
    .nullable()
    .refine((val) => val, {
      message: createSelectRequiredMessage("月"),
    }),
});

// 現在のお仕事について教えてくださいフォームスキーマ
const JOB_INDUSTRY_REQUIRED_CATEGORIES = [
  JobCategory.会社員上場,
  JobCategory.会社員非上場,
  JobCategory.会社員外資系企業,
];

export const accountJobSchema = z.object({
  job_category: z.string().min(1, createSelectRequiredMessage("ご職業")),
  job_industry: z.string().nullable().optional(),
  job_continuous_period: z.string().min(1, createSelectRequiredMessage("勤続年数")),
  annual_income: annualIncomeSchema,
});

// 現在の資産について教えてくださいフォームスキーマ
export const accountFinancialAssetsSchema = z.object({
  financial_asset: z.string().min(1, createSelectRequiredMessage("金融資産（預金・株式など）")),
  holding_property_count: z
    .string()
    .min(1, createRequiredMessage("投資用不動産保有数"))
    .regex(HALF_WIDTH_NUMBER_REGEX, HALF_WIDTH_NUMBER_FORMAT_MESSAGE),
  real_estate_loan_amount: z
    .string()
    .refine((val) => !val || isInteger(val), { message: INTEGER_FORMAT_MESSAGE })
    .refine((val) => !val || isWithinIntLimit(val), { message: PRICE_LIMIT_MESSAGE })
    .refine((val) => !val || isNonNegative(val), { message: NON_NEGATIVE_MESSAGE }),
});

// 今まで不動産投資のご経験はありますかフォームスキーマ
export const accountInterestSchema = z.object({
  has_investment_experience: z.boolean({ required_error: createSelectRequiredMessage("いずれか") }),
  interest_tag_ids: z.array(z.number()).optional(),
  interested_property_type_ids: z.array(z.number()).min(1, createSelectRequiredMessage("最低1つの項目")),
});

// プロパリーを利用しようと思った理由を教えてくださいフォームスキーマ
export const accountReasonSchema = z.object({
  purpose_of_use_ids: z.array(z.number()).min(1, createSelectRequiredMessage("最低1つの項目")),
  purpose_of_use_free_text: z.string().min(1, "理由を入力してください").optional(),
  other_request_text: z.string().optional(),
});

// どこでアプリを知っていただけましたか？フォームスキーマ
export const accountInflowRouteSchema = z.object({
  inflow_route_ids: z.array(z.number()).min(1, createSelectRequiredMessage("最低1つの項目")),
  inflow_route_free_text: z.string().optional(),
});

// 全体のアカウント登録フォームスキーマ
export const accountRegisterSchema = accountBirthSchema
  .merge(accountResidenceSchema)
  .merge(accountJobSchema)
  .merge(accountFinancialAssetsSchema)
  .merge(accountInterestSchema)
  .merge(accountReasonSchema)
  .merge(accountInflowRouteSchema)
  .superRefine((data, ctx) => {
    const needsJobIndustry = JOB_INDUSTRY_REQUIRED_CATEGORIES.includes(data.job_category as JobCategory);
    if (needsJobIndustry && (!data.job_industry || data.job_industry.trim() === "")) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["job_industry"],
        message: createSelectRequiredMessage("業種"),
      });
    }
  });

// 型定義
export type AccountBirthFormData = z.infer<typeof accountBirthSchema>;
export type AccountResidenceFormData = z.infer<typeof accountResidenceSchema>;
export type AccountJobFormData = z.infer<typeof accountJobSchema>;
export type AccountFinancialAssetsFormData = z.infer<typeof accountFinancialAssetsSchema>;
export type AccountInterestFormData = z.infer<typeof accountInterestSchema>;
export type AccountReasonFormData = z.infer<typeof accountReasonSchema>;
export type AccountInflowRouteFormData = z.infer<typeof accountInflowRouteSchema>;
export type AccountRegisterFormData = z.infer<typeof accountRegisterSchema>;
